// AI노트 «요약·메모» TXT 본문 — 백엔드를 거치지 않고 화면이 이미 가진 값으로 브라우저에서 만든다
// (클로바 묶음 C2 · 2026-10-04). 순수 함수: React·API 의존이 없다.

const DEFAULT_TITLE = '제목 없음';
const NO_SUMMARY = '(요약 없음)';
const NO_NOTES = '(메모 없음)';

// 파일 안의 시간 표기는 1시간을 넘으면 H:MM:SS 다 — 화면 fmtDur(M:SS · 3725 → 62:05)와
// 의도적으로 다르다(받아서 열어 보는 문서라 시:분:초가 읽기 쉽다).
const clock = (sec) => {
  const total = Math.max(0, Math.floor(Number(sec) || 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
};

// 정렬 키 — 시간이 없거나 숫자가 아니면 0 으로 본다(예외를 내지 않는다).
const timeKey = (t) => Math.max(0, Number(t) || 0);

// 한 줄로 — 메모 안의 줄바꿈은 공백 하나로 바꾸고 앞뒤를 다듬는다.
const oneLine = (text) => String(text ?? '').replace(/[\r\n]+/g, ' ').trim();

const asObject = (meeting) => (meeting && typeof meeting === 'object' ? meeting : {});

// 녹음 메모(recording_notes) + 전사 구간 메모(transcript[].memo)를 합쳐 시간 오름차순으로.
// 같은 시간이면 녹음 메모가 먼저다 — 녹음 메모를 먼저 담고 Array#sort 의 안정 정렬(ES2019)에 맡긴다.
const collectNotes = (meeting) => {
  const m = asObject(meeting);
  const rows = [];
  const recNotes = Array.isArray(m.recording_notes) ? m.recording_notes : [];
  recNotes.forEach((n) => {
    const text = oneLine(asObject(n).text);
    if (text) rows.push({ sec: timeKey(asObject(n).time), text });
  });
  const segs = Array.isArray(m.transcript) ? m.transcript : [];
  segs.forEach((s) => {
    const text = oneLine(asObject(s).memo);
    if (text) rows.push({ sec: timeKey(asObject(s).start), text });
  });
  return rows.sort((a, b) => a.sec - b.sec);
};

/** 노트 하나의 «요약·메모» TXT 본문을 만든다(제목 · [요약] · [메모] 시간순). */
export function buildSummaryNotesText(meeting) {
  const m = asObject(meeting);
  const title = String(m.title ?? '').trim() || DEFAULT_TITLE;
  const summary = String(m.summary ?? '').trim() || NO_SUMMARY;
  const notes = collectNotes(m);
  const noteLines = notes.length > 0 ? notes.map((n) => `[${clock(n.sec)}] ${n.text}`) : [NO_NOTES];
  return [title, '', '[요약]', summary, '', '[메모]', ...noteLines].join('\n');
}

/** 받을 내용(요약 또는 메모)이 있는지 — 메뉴 항목 노출 판정을 여기 한 곳에만 둔다. */
export function hasSummaryOrNotes(meeting) {
  const m = asObject(meeting);
  return String(m.summary ?? '').trim() !== '' || collectNotes(m).length > 0;
}

// ── 요약 MD·TXT · 요약·메모 MD(2026-10-15) ─────────────────────────────────────
// 요약 카드 «다운로드 ▾»(MD·TXT)와 도구줄 «요약·메모 (MD)» 는 브라우저가 만든다.
// «요약·메모 (DOCX)» 도 아래 마크다운을 서버(POST /api/meetings/{id}/summary-notes.docx)에 보내 바꾼다 —
// 메모 정렬·시간 표기 규칙을 서버에 두 벌 두지 않는다.

const titleOf = (m) => String(m.title ?? '').trim() || DEFAULT_TITLE;

/** 요약 MD 본문 — `# 제목` 머리줄 + 요약 원문(마크다운 그대로). */
export function buildSummaryMarkdown(meeting) {
  const m = asObject(meeting);
  return `# ${titleOf(m)}\n\n${String(m.summary ?? '').trim()}\n`;
}

/**
 * 요약 마크다운 → 평문. 마크다운 표시만 걷어 내고 줄 구조는 그대로 둔다.
 * 목록 기호(- · * · 1.)는 평문에서도 목록으로 읽히므로 남기고, 표 줄은 남기되 구분 줄은 지운다.
 */
export function summaryMarkdownToText(md) {
  const out = [];
  String(md ?? '').split(/\r?\n/).forEach((raw) => {
    const t = raw.trim();
    if (t.startsWith('```')) return;                                        // 코드 펜스 줄
    if (t.startsWith('|') && t.includes('-') && /^[|:\s-]+$/.test(t)) return;  // 표 구분 줄
    out.push(raw
      .replace(/^\s{0,3}#{1,6}\s+/, '')       // 제목 표시
      .replace(/^\s*>\s?/, '')                // 인용 표시
      .replace(/\*\*(.+?)\*\*/g, '$1')        // 굵게 **…**
      .replace(/__(.+?)__/g, '$1')            // 굵게 __…__
      .replace(/`([^`]+)`/g, '$1'));          // 인라인 코드
  });
  return out.join('\n');
}

/** 요약 TXT 본문 — 제목 + 마크다운 표시를 걷어 낸 요약. */
export function buildSummaryText(meeting) {
  const m = asObject(meeting);
  return `${titleOf(m)}\n\n${summaryMarkdownToText(String(m.summary ?? '').trim())}\n`;
}

/** 노트 하나의 «요약·메모» MD 본문(제목 · ## 요약 · ## 메모 시간순 목록). */
export function buildSummaryNotesMarkdown(meeting) {
  const m = asObject(meeting);
  const summary = String(m.summary ?? '').trim() || NO_SUMMARY;
  const notes = collectNotes(m);
  const noteLines = notes.length > 0 ? notes.map((n) => `- [${clock(n.sec)}] ${n.text}`) : [NO_NOTES];
  return [`# ${titleOf(m)}`, '', '## 요약', '', summary, '', '## 메모', '', ...noteLines].join('\n') + '\n';
}
